import { useState } from 'react';
import axios from 'axios';

import { errorText } from '../components/custom_creative_session/DevelopmentStage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * The Psychology Map step's behaviour — ONE implementation, two presentations.
 *
 * WAVE 3, SCREEN 1. v1 and v2 render this step differently and must never
 * decide differently. The alternative is what the resume rule was before wave
 * 2b: two copies of one rule, against one database, disagreeing.
 *
 * THE DEFECT THIS CARRIES THE FIX FOR, swept out before the port and measured
 * live on 2026-09-09: Regenerate called `/psychology-map/generate`, which
 * short-circuits when the offer already has a map and returns the CACHED one
 * without calling a model. The screen appended whatever came back, so every
 * click duplicated the whole map. 4 of 38 offers hold duplicate statements;
 * GRANT001 holds 60 entries with 15 unique — exactly 4.0x, three clicks.
 * Regenerate had never once regenerated.
 */
export const ENTRY_TYPES = [
  'PAIN_POINT', 'EMOTIONAL_TRIGGER', 'IDENTITY_GAP', 'SOCIAL_PRESSURE', 'DESIRE',
];

export function usePsychologyMap({ offer, isNewSession, onComplete }) {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [entries, setEntries] = useState(isNewSession ? [] : (offer.psychology_map || []));
  const [awarenessStage, setAwarenessStage] = useState(offer.awareness_stage_focus || 'PROBLEM_AWARE');
  const [feedback, setFeedback] = useState('');

  // `{ text, count }`. The COUNT exists because a second click that produces the
  // identical sentence is otherwise indistinguishable from a dead button — the
  // Founder hit exactly that on 2026-09-09: clicked Generate again, the message
  // was already on screen unchanged, and it read as "nothing happened at all".
  // Rule 10 does not stop at distinguishing failures from successes; a repeat
  // has to be distinguishable from a no-op too.
  const [mapNote, setMapNoteState] = useState(null);

  // A FAILURE IS NOT A RESULT, and it does not go in the results slot.
  //
  // THE FOUNDER, 2026-09-09: "the Psychology Map step's save-failure still pops
  // an old-style browser alert instead of showing the error on screen — bring
  // it in line with how every other step handles a failed save." He is right,
  // and this was the one handler wave 3 missed: `confirmAndProceed` called
  // `alert()` and pushed the detail into `console.error`, where nobody sees it.
  //
  // A browser dialog is worse here than it looks: it blocks the page, it is the
  // only place the reason appears, and it is gone the moment it is dismissed —
  // so the operator is left on a step that looks saved and is not.
  //
  // `generateMap`'s failure moves here too. It was written into `mapNote`, the
  // same slot a successful result uses, in the same style — a failure wearing
  // the answer's clothes, which is the pattern rule 10 forbids.
  const [mapError, setMapError] = useState(null);
  const setMapNote = (text) => {
    setMapNoteState((prev) =>
      !text ? null
      : prev && prev.text === text ? { text, count: prev.count + 1 }
      : { text, count: 1 });
  };

  // `isRegenerate` is the operator asking again, deliberately.
  //
  // THE DEFECT THIS FIXES, measured live 2026-09-09. The backend short-circuits
  // when the offer already has a map and returns the CACHED one without calling
  // a model. This function appended whatever came back. So Regenerate returned
  // the existing entries and added them to the entries already on screen —
  // every statement duplicated, every click. 4 of 38 offers hold duplicate
  // statements; GRANT001 holds 60 entries with 15 unique, exactly 4.0x.
  //
  // Regenerate had never regenerated. Not once. It only ever duplicated.
  const generateMap = async (isRegenerate = false) => {
    setGenerating(true);
    // NOT cleared here. Clearing first meant the note that follows always saw a
    // null previous value, so the repeat counter reset on every click and a
    // second identical message looked exactly like the first — which is the
    // thing this counter exists to prevent. The note is set exactly once, at
    // the end, from `note` below.
    try {
      const response = await axios.post(`${API}/offers/${offer.offer_id}/psychology-map/generate`, {
        feedback: feedback || undefined,
        refresh: isRegenerate || undefined,
      });
      const returned = response.data.psychology_map || [];
      const wasCached = response.data.status === 'already_exists';

      // THE GUARD KEYS ON WHAT IS ON SCREEN, NOT ON THE STATUS ALONE.
      //
      // A cached map means two completely different things depending on what
      // the operator is looking at:
      //
      //   screen empty      -> this is a LOAD. The cache IS the answer, and the
      //                        only way an existing map ever reaches a NEW
      //                        session's screen. Show it.
      //   screen has rows   -> this is a REPEAT. Add nothing, say so.
      //
      // The first version of this fix keyed on the status alone and returned
      // early either way. That stopped the duplication and broke the loading:
      // on a new session the operator got an empty screen, "nothing was added",
      // and no way to see the offer's map at all. All 38 offers have a map, so
      // every new session hit it. Found live 2026-09-09 — a regression this fix
      // introduced, and the reason the de-duplication below is what actually
      // prevents repeats. It runs on every path now.

      // Append, but never the same statement twice. Accumulating a bigger map
      // across runs is deliberate; accumulating the SAME entries is the bug.
      //
      // Computed from `entries` rather than inside a `setEntries` updater: the
      // updater runs during the render phase, AFTER the code below, so a count
      // taken inside it is always zero when read. The first version of this fix
      // did exactly that and announced "every one repeated" on every successful
      // generation — caught by its own test before it shipped. The button is
      // disabled while generating, so there is no concurrent run to make
      // reading `entries` here stale.
      const seen = new Set(entries.map(e => (e.statement || '').trim()));
      const fresh = returned.filter(e => {
        const key = (e.statement || '').trim();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      const added = fresh.length;
      if (added > 0) setEntries(prev => [...prev, ...fresh]);

      // Rule 10: these are four different outcomes and they used to be two.
      let note = null;
      if (returned.length === 0) {
        note = 'The model returned no entries. Nothing was added.';
      } else if (added === 0) {
        note = wasCached
          ? 'This offer already has a psychology map and it was returned unchanged — ' +
            'nothing new was generated, and everything in it is already on screen.'
          : `The model returned ${returned.length} entr${returned.length === 1 ? 'y' : 'ies'} ` +
            `and every one repeated something already on the map, so none were added.`;
      } else if (wasCached) {
        // The load case. Worth saying, because the operator pressed a button
        // called Generate and no generation happened.
        note = `Loaded this offer's existing psychology map — ${added} ` +
               `entr${added === 1 ? 'y' : 'ies'}. Nothing new was generated; press ` +
               `Regenerate to ask for new entries.`;
      }
      // `null` when entries were genuinely added and nothing needs saying —
      // which also clears a note left from a previous attempt.
      setMapNote(note);
      setFeedback('');
    } catch (error) {
      console.error('Failed to generate psychology map:', error);
      setMapError(`Could not generate the psychology map: ${errorText(error, 'no reason given')}`);
    } finally {
      setGenerating(false);
    }
  };

  const confirmAndProceed = async () => {
    setLoading(true);
    setMapError(null);
    try {
      await axios.post(`${API}/offers/${offer.offer_id}/psychology-map/confirm`, {
        awareness_stage_focus: awarenessStage,
        confirmed_entries: entries
      });
      onComplete();
      return true;
    } catch (error) {
      // NAMED ON SCREEN, and the step does not advance. Both halves matter: a
      // failed save that moved the operator on would leave them working from a
      // map the offer never recorded.
      setMapError(
        `The psychology map was not saved: ${errorText(error, 'no reason given')}. `
        + `Nothing has been lost — your edits are still on screen.`);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const togglePriority = (idx) => {
    const updated = [...entries];
    updated[idx].priority_flagged = !updated[idx].priority_flagged;
    setEntries(updated);
  };

  const removeEntry = (idx) => {
    setEntries(entries.filter((_, i) => i !== idx));
  };

  const updateEntryEmotion = (idx, emotion) => {
    const updated = [...entries];
    updated[idx].primary_emotion = emotion;
    setEntries(updated);
  };


  return {
    entries, setEntries, loading, generating, feedback, setFeedback,
    awarenessStage, setAwarenessStage, mapNote, mapError, setMapError,
    generateMap, confirmAndProceed, togglePriority, removeEntry,
    updateEntryEmotion,
  };
}
